import { useCallback, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';

import { usePreferences } from '@/hooks/usePreferences';
import { BrainFilterViews, FilterView, LogsAnalytics } from '@/shared/types/analytics';

import { createMutableCopy, filtersEqual } from './sessionFilterUtils';

export const useFilterViews = (brainId: string, currentFilters: LogsAnalytics[]) => {
  const { t } = useTranslation();
  const { accountUserPrefs, updateAccountUserPreferences, updateAccountUserPreferencesStatus } =
    usePreferences();
  const [activeViewId, setActiveViewId] = useState<string | undefined>();

  // Get views from preferences - memoized to prevent dependency changes
  const brainFilterViews = useMemo(
    () =>
      (accountUserPrefs?.brain_filter_views as unknown as
        Record<string, BrainFilterViews> | undefined) || {},
    [accountUserPrefs?.brain_filter_views]
  );
  const filterViewsData = useMemo(
    () => brainFilterViews[brainId] || { views: [] },
    [brainFilterViews, brainId]
  );
  const views = useMemo(() => filterViewsData.views || [], [filterViewsData.views]);

  // Save a new filter view or update an existing one
  const saveFilterView = useCallback(
    async (name: string, overwriteId?: string) => {
      if (!name || name.trim().length === 0) {
        toast.error(t('brain_logs.filter_view_name_required'));
        return;
      }

      if (name.length > 50) {
        toast.error(t('brain_logs.filter_view_name_too_long'));
        return;
      }

      const filtersToPersist = createMutableCopy(currentFilters);
      let updatedViews: FilterView[];

      if (overwriteId) {
        // Update existing view
        updatedViews = views.map((view) =>
          view.id === overwriteId ? { ...view, name: name.trim(), filters: filtersToPersist } : view
        );
        toast.success(t('brain_logs.filter_view_updated', { 0: name.trim() }));
      } else {
        // Create new view
        const newView: FilterView = { id: uuidv4(), name: name.trim(), filters: filtersToPersist };
        updatedViews = [...views, newView];
        setActiveViewId(newView.id);
        toast.success(t('brain_logs.filter_view_saved', { 0: name.trim() }));
      }

      // Update preferences - only send views array (no timestamps or activeViewId)
      const updatedPrefs = {
        ...(accountUserPrefs || {}),
        brain_filter_views: {
          ...((accountUserPrefs?.brain_filter_views as unknown as Record<
            string,
            BrainFilterViews
          >) || {}),
          [brainId]: {
            views: updatedViews.map(({ id, name, filters }) => ({ id, name, filters })),
          },
        },
      };
      updateAccountUserPreferences(
        updatedPrefs as unknown as Parameters<typeof updateAccountUserPreferences>[0]
      );
    },
    [views, currentFilters, accountUserPrefs, updateAccountUserPreferences, brainId, t]
  );

  // Load a filter view
  const loadFilterView = useCallback(
    (viewId: string) => {
      const view = views.find((v) => v.id === viewId);
      if (view) {
        setActiveViewId(viewId);
        // Don't update preferences when loading - just track locally
        return view.filters;
      }
      return null;
    },
    [views]
  );

  // Delete a filter view
  const deleteFilterView = useCallback(
    async (viewId: string) => {
      const view = views.find((v) => v.id === viewId);
      if (!view) {
        return;
      }

      const updatedViews = views.filter((v) => v.id !== viewId);

      // Clear active view if it was deleted
      if (activeViewId === viewId) {
        setActiveViewId(undefined);
      }

      // Update preferences - only send views array (no activeViewId)
      const updatedPrefs = {
        ...(accountUserPrefs || {}),
        brain_filter_views: {
          ...((accountUserPrefs?.brain_filter_views as unknown as Record<
            string,
            BrainFilterViews
          >) || {}),
          [brainId]: {
            views: updatedViews.map(({ id, name, filters }) => ({ id, name, filters })),
          },
        },
      };
      updateAccountUserPreferences(
        updatedPrefs as unknown as Parameters<typeof updateAccountUserPreferences>[0]
      );

      toast.success(t('brain_logs.filter_view_deleted', { 0: view.name }));
    },
    [views, activeViewId, accountUserPrefs, updateAccountUserPreferences, brainId, t]
  );

  // Check if current filters have unsaved changes compared to active view
  const hasUnsavedChanges = useMemo(() => {
    if (!activeViewId) {
      // If no view is active, check if there are any filters
      return currentFilters.length > 0;
    }

    const activeView = views.find((v) => v.id === activeViewId);
    if (!activeView) {
      return false;
    }

    return !filtersEqual(currentFilters, activeView.filters);
  }, [currentFilters, activeViewId, views]);

  // Get the currently active view
  const activeView = useMemo(() => {
    return views.find((v) => v.id === activeViewId);
  }, [views, activeViewId]);

  return {
    views,
    activeView,
    activeViewId,
    hasUnsavedChanges,
    saveFilterView,
    loadFilterView,
    deleteFilterView,
    setActiveViewId,
    deleteStatus: updateAccountUserPreferencesStatus,
  };
};
